import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RepeatIcon from '@mui/icons-material/Repeat';
import PersonIcon from '@mui/icons-material/Person';
import dayjs, { Dayjs } from 'dayjs';
import { Task, TaskStatus, Store, Staff } from '../types';
import { tasksAPI, lookupAPI } from '../services/api';
import TaskModal from './TaskModal';
import TaskScheduler from './TaskScheduler';

interface TaskKanbanProps {
  selectedStore?: Store | null;
}

// Status configuration
const STATUSES: { key: TaskStatus; label: string; color: string; bgColor: string; borderColor: string }[] = [
  { key: 'todo', label: 'To Do', color: '#7a5d00', bgColor: '#fff3e0', borderColor: '#ff9800' },
  { key: 'in_progress', label: 'In Progress', color: '#0d47a1', bgColor: '#e3f2fd', borderColor: '#1976d2' },
  { key: 'done', label: 'Done', color: '#1b5e20', bgColor: '#e8f5e9', borderColor: '#4caf50' },
];

// Priority colors
const PRIORITY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  Low: { bg: '#e8f5e9', border: '#4caf50', text: '#1b5e20' },
  Medium: { bg: '#fff8e1', border: '#ffc107', text: '#7a5d00' },
  High: { bg: '#ffebee', border: '#f44336', text: '#c62828' },
};

// Task Card Component
interface TaskCardProps {
  task: Task;
  onEdit: (task: Task) => void;
  onStatusChange: (taskId: number, status: TaskStatus) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onEdit, onStatusChange }) => {
  const statusConfig = STATUSES.find(s => s.key === task.status) || STATUSES[0];
  const priorityConfig = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.Medium;

  const isOverdue = task.is_overdue;

  const dateLabel = useMemo(() => {
    if (task.instance_date) return dayjs(task.instance_date).format('DD/MM');
    if (task.start_date) return dayjs(task.start_date).format('DD/MM');
    return null;
  }, [task]);

  return (
    <Card
      sx={{
        mb: 1.5,
        cursor: 'pointer',
        borderLeft: `4px solid ${isOverdue ? '#f44336' : statusConfig.borderColor}`,
        bgcolor: isOverdue ? '#ffebee' : '#fff',
        '&:hover': {
          boxShadow: 3,
        },
        transition: 'box-shadow 0.2s',
      }}
      onClick={() => onEdit(task)}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
            {task.title}
          </Typography>
          <Chip
            label={task.priority}
            size="small"
            sx={{
              bgcolor: priorityConfig.bg,
              color: priorityConfig.text,
              border: `1px solid ${priorityConfig.border}`,
              fontSize: '0.7rem',
              height: 20,
              ml: 1,
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {task.parent_task_id && (
            <Chip
              icon={<RepeatIcon sx={{ fontSize: 14 }} />}
              label="Recurring"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 18 }}
            />
          )}
          {isOverdue && (
            <Chip
              label="OVERDUE"
              size="small"
              sx={{
                bgcolor: '#f44336',
                color: '#fff',
                fontSize: '0.65rem',
                height: 18,
                fontWeight: 700,
              }}
            />
          )}
          {task.assignee_name && (
            <Chip
              icon={<PersonIcon sx={{ fontSize: 14 }} />}
              label={task.assignee_name}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 18 }}
            />
          )}
          {dateLabel && (
            <Chip
              label={dateLabel}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 18 }}
            />
          )}
        </Box>

        <FormControl size="small" fullWidth onClick={(e) => e.stopPropagation()}>
          <Select
            value={task.status}
            onChange={(e) => onStatusChange(task.id!, e.target.value as TaskStatus)}
            sx={{ fontSize: '0.75rem', height: 28 }}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s.key} value={s.key} sx={{ fontSize: '0.75rem' }}>
                Move to {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </CardContent>
    </Card>
  );
};

const TaskKanban: React.FC<TaskKanbanProps> = ({ selectedStore }) => {
  const [viewMode, setViewMode] = useState<'board' | 'schedule'>('board');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterStoreId, setFilterStoreId] = useState<string>('all');
  const [filterDateFrom, setFilterDateFrom] = useState<Dayjs | null>(null);
  const [filterDateTo, setFilterDateTo] = useState<Dayjs | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Partial<Task> | undefined>(undefined);

  // Refresh trigger for scheduler
  const [refreshKey, setRefreshKey] = useState(0);

  // Load stores and staff
  useEffect(() => {
    const loadLookupData = async () => {
      try {
        const [storesData, staffData] = await Promise.all([
          lookupAPI.getStores(),
          lookupAPI.getStaff(),
        ]);
        setStores(storesData);
        setStaff(staffData);

        // Set default store filter based on selected store
        if (selectedStore) {
          setFilterStoreId(selectedStore.id.toString());
        }
      } catch (err) {
        console.error('Error loading lookup data:', err);
      }
    };
    loadLookupData();
  }, [selectedStore]);

  // Load tasks
  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // First, process any due recurring tasks
      await tasksAPI.processRecurring();

      // Then load tasks
      const filters: any = {
        exclude_parents: true,
      };
      if (filterStoreId && filterStoreId !== 'all') {
        filters.store_id = filterStoreId;
      }
      if (filterDateFrom) {
        filters.date_from = filterDateFrom.format('YYYY-MM-DD');
      }
      if (filterDateTo) {
        filters.date_to = filterDateTo.format('YYYY-MM-DD');
      }

      const tasksData = await tasksAPI.getAll(filters);
      setTasks(tasksData);
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [filterStoreId, filterDateFrom, filterDateTo]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Group tasks by status
  const tasksByStatus = useMemo(() => {
    const grouped: Record<TaskStatus, Task[]> = {
      todo: [],
      in_progress: [],
      done: [],
    };

    tasks.forEach((task) => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      }
    });

    return grouped;
  }, [tasks]);

  // Handlers
  const handleCreateTask = () => {
    setEditingTask({
      store_id: filterStoreId !== 'all' ? parseInt(filterStoreId) : selectedStore?.id,
    });
    setModalOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setModalOpen(true);
  };

  const handleSaveTask = async (taskData: any) => {
    try {
      if (taskData.id) {
        await tasksAPI.update(taskData.id, taskData);
      } else {
        await tasksAPI.create(taskData);
      }
      loadTasks();
      setRefreshKey(k => k + 1); // Trigger scheduler refresh
    } catch (err) {
      console.error('Error saving task:', err);
      setError('Failed to save task');
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      await tasksAPI.delete(taskId);
      loadTasks();
      setRefreshKey(k => k + 1); // Trigger scheduler refresh
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task');
    }
  };

  const handleStatusChange = async (taskId: number, status: TaskStatus) => {
    try {
      await tasksAPI.updateStatus(taskId, status);
      loadTasks();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  const handleSchedulerTaskCreate = (date: string) => {
    setEditingTask({
      store_id: filterStoreId !== 'all' ? parseInt(filterStoreId) : selectedStore?.id,
      schedule_type: 'single',
      start_date: date,
    });
    setModalOpen(true);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Task Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tabs
            value={viewMode}
            onChange={(_, v) => setViewMode(v)}
            sx={{ minHeight: 36 }}
          >
            <Tab
              value="board"
              icon={<ViewKanbanIcon />}
              iconPosition="start"
              label="Board"
              sx={{ minHeight: 36, py: 0 }}
            />
            <Tab
              value="schedule"
              icon={<CalendarMonthIcon />}
              iconPosition="start"
              label="Schedule"
              sx={{ minHeight: 36, py: 0 }}
            />
          </Tabs>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Store</InputLabel>
              <Select
                value={filterStoreId}
                onChange={(e) => setFilterStoreId(e.target.value)}
                label="Store"
              >
                <MenuItem value="all">All Stores</MenuItem>
                {stores.map((s) => (
                  <MenuItem key={s.id} value={s.id.toString()}>
                    {s.store_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="From Date"
              value={filterDateFrom}
              onChange={(date) => setFilterDateFrom(date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
              format="DD/MM/YYYY"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="To Date"
              value={filterDateTo}
              onChange={(date) => setFilterDateTo(date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
              format="DD/MM/YYYY"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={loadTasks} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateTask}
                fullWidth
              >
                Add Task
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Kanban Board View */}
      {!loading && viewMode === 'board' && (
        <Grid container spacing={2}>
          {STATUSES.map((status) => (
            <Grid item xs={12} md={4} key={status.key}>
              <Paper
                sx={{
                  bgcolor: status.bgColor,
                  border: `1px solid ${status.borderColor}`,
                  borderRadius: 2,
                  minHeight: 400,
                  p: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: status.color }}
                  >
                    {status.label}
                  </Typography>
                  <Chip
                    label={tasksByStatus[status.key].length}
                    size="small"
                    sx={{
                      bgcolor: '#fff',
                      border: `1px solid ${status.borderColor}`,
                      color: status.color,
                      fontWeight: 600,
                    }}
                  />
                </Box>

                <Box>
                  {tasksByStatus[status.key].map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onEdit={handleEditTask}
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                  {tasksByStatus[status.key].length === 0 && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ textAlign: 'center', py: 4, opacity: 0.6 }}
                    >
                      No tasks
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Scheduler View */}
      {!loading && viewMode === 'schedule' && (
        <TaskScheduler
          storeId={filterStoreId}
          onTaskClick={handleEditTask}
          onDayClick={handleSchedulerTaskCreate}
          onTemplateClick={handleEditTask}
          onTemplateDelete={handleDeleteTask}
          refreshKey={refreshKey}
        />
      )}

      {/* Task Modal */}
      <TaskModal
        open={modalOpen}
        task={editingTask}
        selectedStore={selectedStore}
        onClose={() => {
          setModalOpen(false);
          setEditingTask(undefined);
        }}
        onSave={handleSaveTask}
        onDelete={handleDeleteTask}
      />
    </Box>
  );
};

export default TaskKanban;
